import { aICopywriterPostSchema } from '$lib/types/AICopywriter/types';
import type { MessageContent } from '@AlidaPackages/ailabs-client';
import { z } from 'zod';

const aICopywriterMessageBaseSchema = z.object({
	type: z.literal('json'),
	dataType: z.string(),
});

const aICopywriterResponseMessageSchema = aICopywriterMessageBaseSchema.extend({
	text: z.object({
		post: aICopywriterPostSchema,
	}),
});

const aICopywriterRequestMessageSchema = aICopywriterMessageBaseSchema.extend({
	text: z.object({
		prompt: z.string(),
		post: aICopywriterPostSchema.optional(),
	}),
});

export type AICopywriterResponseMessage = z.infer<typeof aICopywriterResponseMessageSchema>;
export type AICopywriterRequestMessage = z.infer<typeof aICopywriterRequestMessageSchema>;

/**
 * Safely parses and validates AICopywriter response messages from the AI Labs client.
 *
 * This function performs JSON parsing on the message text and validates it against
 * the AICopywriterResponseMessage schema. It handles parsing errors gracefully
 * and returns null for invalid or malformed messages.
 *
 * @param message - Raw MessageContent from AI Labs client containing a stringified JSON object in the text field.
 * @returns Validated AICopywriterResponseMessage object, or null if parsing/validation fails
 *
 * @example
 * ```typescript
 * const response = parseAICopywriterResponse(messageFromAILabs);
 * if (response) {
 *   // Safe to use response.text.post
 *   console.log(response.text.post);
 * }
 * ```
 */
export function parseAICopywriterResponse(message: MessageContent) {
	try {
		const messageWithParsedText = {
			...message,
			text: JSON.parse(message.text),
		};

		const result = aICopywriterResponseMessageSchema.safeParse(messageWithParsedText);

		// If the message is not a valid AICopywriterResponseMessage, return null
		if (!result.success) {
			return null;
		}

		return result.data;
	} catch (e) {
		// If text field is not a valid JSON, return null
		return null;
	}
}
